import React, { useEffect } from 'react';
import Dialog, { DialogActionBar } from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import FormTextBox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import { Field, Form } from 'react-final-form';
import { LandSystem } from 'src/models/LandSystem';
import SurfaceTract from 'src/models/SurfaceTract';
import TractSearch from '../../TractComponents/TractSearch';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import useFetch from 'use-http';
import { Url } from 'src/api';
import FormRow from 'src/components/controls/forms/FormRow';
import FormTextArea from 'src/components/controls/forms/redesign/FormTextArea';
import moment from 'moment';
import Tract from 'src/models/Tract';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import County from 'src/models/County';
import NewTract from 'src/models/tract_detail/NewTract';
import { ODataOptions } from 'src/util/ODataBuilder';

interface ICreateSurfaceTractProps {
    landSystem: LandSystem;
    fieldNumber?: number;
    isSurfaceProject?: boolean;
    counties: Array<County>;
    tractType?: 'Mineral' | 'Surface';
    onCloseDialog?: () => void;
    onSurfaceTractCreated: (surfaceTract: SurfaceTract) => void;
    onMineralTractCreated: (mineralTract: Tract) => void;
}

type Pages = 'Search' | 'New' | 'Unattached';

const CreateSurfaceTractDialog: React.FC<ICreateSurfaceTractProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const [page, setPage] = React.useState<Pages>('Search');
    const [selectedSurfaceTract, setSelectedSurfaceTract] = React.useState<SurfaceTract>(undefined);
    const [selectedMineralTract, setSelectedMineralTract] = React.useState<Tract>(undefined);
    const { post, response } = useFetch();

    useEffect(() => {
        let isUnattached = (props.isSurfaceProject && props.tractType === 'Surface') || (!props.isSurfaceProject && props.tractType === 'Mineral');
        if (isUnattached) {
            setPage('Unattached');
        }
    }, [props.isSurfaceProject, props.tractType]);

    const attachTract = () => {
        if (props.tractType === 'Surface') {
            attachSurfaceTract(selectedSurfaceTract);
        } else if (props.tractType === 'Mineral') {
            attachMineralTract(selectedMineralTract);
        }
    };

    const attachSurfaceTract = async (st: SurfaceTract) => {
        await post(`${Url.tract.getTract(appContext)}/surfaceTracts/attach`, st);

        if (response.ok) {
            props.onSurfaceTractCreated(selectedSurfaceTract);
            props.onCloseDialog();
        }
    };
    
    const createSurfaceTract = async (val: SurfaceTract) => {
        let st = new SurfaceTract();
        if (props.isSurfaceProject) {
            st = formatSurfaceTract(val);
        } else {
            st.name = val.name;
        }    

        const result = await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${props.fieldNumber}/tracts/surfaceTracts`, st);

        if (response.ok) {
            st.id = result.id;
            props.onSurfaceTractCreated(st);
            props.onCloseDialog();
        }    
    };    

    const attachMineralTract = async (mt: Tract) => {
        await post(`${Url.tract.getTracts(appContext, props.fieldNumber, '')}/surface-tracts/${appContext.surfaceTractNumber}/attach`, mt);

        if (response.ok) {
            props.onMineralTractCreated(selectedMineralTract);
            props.onCloseDialog();
        }
    };

    const createMineralTract = async (val: NewTract) => {
        let mt = new NewTract();
        if (props.isSurfaceProject) {
            mt.tractName = val.name;
        } else {
            mt = formatMineralTract(val);
        }

        let newTractInfo = await post(`${Url.tract.getTracts(appContext, props.fieldNumber, (new ODataOptions).toString())}/create-tract`, mt);

        if (response.ok) {
            if (props.isSurfaceProject) {
                let newTract = new Tract();
                newTract.id = newTractInfo.tractId;
                attachMineralTract(newTract);
            }
            props.onMineralTractCreated(null);
            props.onCloseDialog();
        }
    };

    const formatSurfaceTract = (st: SurfaceTract) => {
        st.id = st.id ? parseInt(st.id + '', 10) : 0;
        st.recordTypeId = st.recordTypeId ? parseInt(st.recordTypeId + '', 10) : null;
        st.instrumentTypeId = st.instrumentTypeId ? parseInt(st.instrumentTypeId + '', 10) : null;
        st.acres = st.acres ? parseFloat(st.acres + '') : 0;
        st.partialAcreage = st.partialAcreage ? parseFloat(st.partialAcreage + '') : 0;

        st.isPartial = st.isPartial ? st.isPartial : false;

        st.name = st.name ? st.name : '';
        st.grantee = st.grantee ? st.grantee : '';
        st.grantor = st.grantor ? st.grantor : '';
        st.book = st.book ? st.book : '';
        st.page = st.page ? st.page : '';
        st.boundByNorth = st.boundByNorth ? st.boundByNorth : '';
        st.boundByEast = st.boundByEast ? st.boundByEast : '';
        st.boundBySouth = st.boundBySouth ? st.boundBySouth : '';
        st.boundByWest = st.boundByWest ? st.boundByWest : '';
        st.legalDescription = st.legalDescription ? st.legalDescription : '';

        st.recordDate = st.recordDate
            ? (moment(st.recordDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        return st;
    };

    const formatMineralTract = (mt: NewTract) => {
        mt.tractName = mt.tractName ? mt.tractName : '';
        mt.grossAcres = mt.grossAcres ? mt.grossAcres : 0;
        mt.countyId = mt.countyId ? mt.countyId : null;
        mt.legalDescription = mt.legalDescription ? mt.legalDescription : '';

        return mt;
    };

    const SearchTractPage = () => (
        <>
            <TractSearch landSystem={props.landSystem} tractType={props.tractType} onSurfaceTractSelected={setSelectedSurfaceTract} onMineralTractSelected={setSelectedMineralTract} />
            <DialogActionBar>
                <Button
                    intent="primary"
                    onClick={() => setPage('New')}
                    text={`Create New ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                />
                <Button
                    intent="primary"
                    disabled={selectedSurfaceTract === undefined}
                    onClick={() => attachTract()}
                    text={`Use Selected ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                />
                <Button intent="danger" onClick={props.onCloseDialog} text="Cancel" />
            </DialogActionBar>
        </>
    );

    const NewTractPage = () => (
        <>
            {props.tractType === 'Surface' && (
                <Form
                    onSubmit={createSurfaceTract}
                    render={({ handleSubmit }) => {
                        return (
                            <>
                                <form onSubmit={handleSubmit}>
                                    <div>Tract Type Surface</div>
                                    <FormTextBox label={props.landSystem === LandSystem.Eastern ? 'Parcel Id' : 'Tract Name'} name="name" />
                                    <DialogActionBar>
                                        <Button
                                            intent="primary"
                                            type="submit"
                                            text={`Create New ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                                        />
                                        <Button intent="danger" onClick={() => setPage('Search')} text="Back" />
                                        <Button intent="danger" onClick={props.onCloseDialog} text="Cancel" />
                                    </DialogActionBar>
                                </form>
                            </>
                        );
                    }}
                />
            )}
            {props.tractType === 'Mineral' && (
                <Form
                    onSubmit={createMineralTract}
                    render={({ handleSubmit }) => {
                        return (
                            <>
                                <div>Tract Type Mineral</div>
                                <form onSubmit={handleSubmit}>
                                    <FormTextBox label={props.landSystem === LandSystem.Eastern ? 'Parcel Id' : 'Tract Name'} name="name" />
                                    <DialogActionBar>
                                        <Button
                                            intent="primary"
                                            type="submit"
                                            text={`Create New ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                                        />
                                        <Button intent="danger" onClick={() => setPage('Search')} text="Back" />
                                        <Button intent="danger" onClick={props.onCloseDialog} text="Cancel" />
                                    </DialogActionBar>
                                </form>
                            </>
                        );
                    }}
                />            
            )}
        </>
    );

    const FullFormPage = () => (
        <>
            <Form
                onSubmit={createSurfaceTract}
                render={({ handleSubmit }) => {
                    return (
                        <>
                            <form onSubmit={handleSubmit}>
                                <div style={{ padding: '1rem', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'none' }}>
                                        <Field name={`id`} component="input" />
                                    </div>
                                    <FormRow>
                                        <FormTextBox label={'Tract Name'} name={`name`} />
                                        <FormTextBox type={FormTextBoxDataType.Float} label="Acres" name={`acres`} />
                                        <FormSelect label="County" name="countyId" items={props.counties ? props.counties : []} />
                                    </FormRow>
                                    <FormRow className="row">
                                        <FormTextArea
                                            height="10.6rem"
                                            label="Legal Description"
                                            name={`legalDescription`}
                                            style={{ width: '100%' }}
                                        />
                                    </FormRow>
                                    <div style={{ clear: 'both' }} />
                                </div>
                                <DialogActionBar>
                                    <Button
                                        intent="primary"
                                        type="submit"
                                        text={`Create New ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                                    />
                                    <Button intent="danger" onClick={props.onCloseDialog} text="Cancel" />
                                </DialogActionBar>
                            </form>
                        </>
                    );
                }}
            />
        </>
    );

    return (
        <Dialog title={`Create ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}>
            {page === 'Search' && SearchTractPage()}
            {page === 'New' && NewTractPage()}
            {page === 'Unattached' && FullFormPage()}
        </Dialog>
    );
};

export default CreateSurfaceTractDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/CreateTractDialog.tsx